import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2 } from "lucide-react";

interface Extra {
  name: string;
  price: number;
  description?: string;
  required: boolean;
  max_qty?: number;
  image_url?: string;
}

interface ExtrasTabProps {
  extras: Extra[];
  onUpdate: (extras: Extra[]) => void;
}

export default function ExtrasTab({ extras, onUpdate }: ExtrasTabProps) {
  const addExtra = () => {
    onUpdate([
      ...extras,
      { name: "", price: 0, description: "", required: false, max_qty: 1 },
    ]);
  };

  const updateExtra = (index: number, field: keyof Extra, value: any) => {
    const updated = [...extras];
    updated[index] = { ...updated[index], [field]: value };
    onUpdate(updated);
  };

  const removeExtra = (index: number) => {
    onUpdate(extras.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Event Extras & Add-Ons</CardTitle>
            <Button onClick={addExtra} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Add Extra
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {extras.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              No extras configured. Click "Add Extra" to add items like t-shirts, parking passes, or meals.
            </p>
          ) : (
            extras.map((extra, index) => (
              <Card key={index} className="border-border/50">
                <CardContent className="pt-4 space-y-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 space-y-4">
                      <div>
                        <Label>Item Name</Label>
                        <Input
                          placeholder="e.g., T-Shirt, Parking Pass, Lunch"
                          value={extra.name}
                          onChange={(e) => updateExtra(index, "name", e.target.value)}
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label>Price (cents)</Label>
                          <Input
                            type="number"
                            placeholder="1500"
                            value={extra.price || ""}
                            onChange={(e) => updateExtra(index, "price", parseInt(e.target.value) || 0)}
                          />
                        </div>
                        <div>
                          <Label>Max Quantity Per Registration</Label>
                          <Input
                            type="number"
                            placeholder="5"
                            value={extra.max_qty || ""}
                            onChange={(e) => updateExtra(index, "max_qty", parseInt(e.target.value) || undefined)}
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Description (optional)</Label>
                        <Textarea
                          placeholder="Item description..."
                          value={extra.description || ""}
                          onChange={(e) => updateExtra(index, "description", e.target.value)}
                          rows={2}
                        />
                      </div>
                      <div>
                        <Label>Image URL (optional)</Label>
                        <Input
                          type="url"
                          placeholder="https://..."
                          value={extra.image_url || ""}
                          onChange={(e) => updateExtra(index, "image_url", e.target.value)}
                        />
                      </div>
                      <div className="flex items-center justify-between pt-2">
                        <Label htmlFor={`required-${index}`}>Required Item</Label>
                        <Switch
                          id={`required-${index}`}
                          checked={extra.required}
                          onCheckedChange={(checked) => updateExtra(index, "required", checked)}
                        />
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeExtra(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}
